import React, { useEffect, useState } from "react";
import { Collapse, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { CommandButton } from "./CommandButton";
import { ReactComponent as SmartRTL } from "../assets/svg/SmartRTL.svg";
import { ReactComponent as Download } from "../assets/svg/Download.svg";
import { ReactComponent as Land } from "../assets/svg/Land.svg";
import { ReactComponent as RTL } from "../assets/svg/RTL.svg";
import { ReactComponent as Pause } from "../assets/svg/Pause.svg";
import { ReactComponent as Play } from "../assets/svg/Play.svg";
import { ReactComponent as Chute } from "../assets/svg/Chute.svg";
import { useAppSelector, useAppDispatch } from "../app/hooks";
import { selectAircraftVitalsById } from "../features/aircraft/aircraftSelectors";
import { issueCommandThunk } from "../features/aircraft/aircraftSlice";

import { SlideToConfirm } from "./SlideToConfirm";
import { commandState } from "./ConfirmCommand";
import { Command, FlightStatus } from "../features/aircraft/types";

export const CommandTray = (props: {
  uavId: string;
  uavState?: string;
  flightMode?: string;
  stale?: boolean;
  flightPhase: FlightStatus | null;
  showFts: boolean;
  cancelFts: () => void;
  isCountingDown?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const vitals = useAppSelector((state) => selectAircraftVitalsById(state, props.uavId));
  const [command, setCommand] = useState<Command | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [delivered, setDelivered] = useState<boolean>(false);

  const [showPause, setShowPause] = useState<boolean>(true);

  const updateCommand = (newCommand: Command) => {
    if (newCommand !== null && newCommand === command) {
      setCommand(null);
    } else {
      setCommand(newCommand);
    }

    if (command === "COMMAND_PARACHUTE") {
      props.cancelFts();
    }
  };

  useEffect(() => {
    return () => {
      props.cancelFts();
    };
  }, []);

  useEffect(() => {
    setCommand((currentCommand) => {
      if (props.showFts) {
        return "COMMAND_PARACHUTE";
      } else if (!props.showFts && currentCommand === "COMMAND_PARACHUTE") {
        return null;
      } else if (
        props.flightPhase === "pendingDelivery" &&
        currentCommand !== "COMMAND_DELIVER"
      ) {
        return "COMMAND_DELIVER";
      } else if (
        props.flightPhase !== "pendingDelivery" &&
        currentCommand === "COMMAND_DELIVER"
      ) {
        return null;
      }
      return currentCommand;
    });
  }, [props.flightPhase, props.showFts]);

  useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.closest("[data-command-tray]") ||
        target.closest("[data-command-menu]")
      ) {
        return;
      }

      setCommand((currentCommand) => {
        if (currentCommand && currentCommand !== "COMMAND_DELIVER") {
          return null;
        }
        return currentCommand;
      });

      if (props.showFts) {
        props.cancelFts();
      }
    };

    window.addEventListener("click", handleClickAway);
    return () => {
      window.removeEventListener("click", handleClickAway);
    };
  }, [command, props.showFts, props.cancelFts]);

  const smartRtl = (e: React.MouseEvent<HTMLElement>) => {
    updateCommand("COMMAND_SMARTRTL");
  };
  const abort = (e: React.MouseEvent<HTMLElement>) => {
    updateCommand("COMMAND_ABORT");
  };
  const rtl = (e: React.MouseEvent<HTMLElement>) => {
    updateCommand("COMMAND_RTL");
  };
  const para = (e: React.MouseEvent<HTMLElement>) => {
    updateCommand("COMMAND_PARACHUTE");
  };
  const land = (e: React.MouseEvent<HTMLElement>) => {
    updateCommand("COMMAND_LAND");
  };
  const pause = (e: React.MouseEvent<HTMLElement>) => {
    updateCommand("COMMAND_PAUSE");
  };
  const resume = (e: React.MouseEvent<HTMLElement>) => {
    updateCommand("COMMAND_RESUME");
  };
  const pauseComplete = () => {
    setShowPause(true);
  };
  const deliver = (e: React.MouseEvent<HTMLElement>) => {
    updateCommand("COMMAND_DELIVER");
  };
  const issueCommand = async () => {
    try {
      if (command) {
        setLoading(true);
        await dispatch(
          issueCommandThunk({ uavId: props.uavId, command })
        ).unwrap();
        setCommand(null);
        setLoading(false);
        props.cancelFts();

        if (command === "COMMAND_PAUSE") {
          setShowPause(false);
        } else if (command === "COMMAND_RESUME") {
          setShowPause(true);
        }
        if (command === "COMMAND_DELIVER") {
          setDelivered(true);
          setCommand(null);
        }
        props.cancelFts();
      }
    } catch (err) {
      setLoading(false);
      setCommand(null);
      console.log(err);
    }
  };

  const getTrayState = () => {
    if (!vitals.uavSubState?.includes("STATE_FLIGHT")) return "GROUNDED";
  };

  if (getTrayState() === "GROUNDED") {
    return null;
  }

  const renderPauseLand = () => {
    return (
      <>
        <Box display={"flex"}>
          <CommandButton
            text={"Land"}
            icon={Land}
            onClick={land}
            active={command === "COMMAND_LAND"}
            loading={command === "COMMAND_LAND" && loading}
          />
        </Box>
        <Box display={"flex"}>
          {showPause ? (
            <CommandButton
              text={"Pause"}
              icon={Pause}
              onClick={pause}
              active={command === "COMMAND_PAUSE"}
              loading={command === "COMMAND_PAUSE" && loading}
            />
          ) : (
            <CommandButton
              text={"Resume"}
              icon={Play}
              onClick={resume}
              active={command === "COMMAND_RESUME"}
              loading={command === "COMMAND_RESUME" && loading}
              timer
              countdownFrom={30}
              onCompleteCountdown={pauseComplete}
            />
          )}
        </Box>
      </>
    );
  };

  return (
    <>
      <Box
        data-command-tray
        display={"flex"}
        justifyContent="space-around"
        pt={2}
        px={1}
      >
        {props.flightPhase === "pendingDelivery" &&
        !delivered &&
        props.uavState !== "ABORT" ? (
          <Box display={"flex"}>
            <CommandButton
              ripple={!command}
              active={command === "COMMAND_DELIVER"}
              text={"Deliver"}
              icon={Download}
              onClick={deliver}
              loading={command === "COMMAND_DELIVER" && loading}
              timer={props.isCountingDown}
              countdownFrom={20}
              preserveTimerSpace={true}
            />
          </Box>
        ) : null}

        <Box display={"flex"}>
          <CommandButton
            active={
              props.flightMode === "AUTO_RTL" ||
              props.flightMode === "SMART_RTL" ||
              props.flightMode === "CMODE(21)" ||
              props.flightMode === "CMODE(27)" ||
              command === "COMMAND_ABORT"
            }
            text={"ABORT"}
            icon={SmartRTL}
            onClick={abort}
            loading={command === "COMMAND_ABORT" && loading}
          />
        </Box>

        {props.showFts ? (
          <Box display={"flex"}>
            <CommandButton
              text={"FTS"}
              icon={Chute}
              onClick={para}
              active={props.showFts}
              loading={command === "COMMAND_PARACHUTE" && loading}
            />
          </Box>
        ) : null}
      </Box>
      <Box p={1}></Box>
      <Collapse in={!!command} unmountOnExit>
        <SlideToConfirm
          key={`${command}-slide-to-confirm`}
          style={{ height: 56 }}
          onConfirm={issueCommand}
          icon={command ? commandState?.[command]?.icon : ""}
          warning={command === "COMMAND_PARACHUTE"}
          ripple={!!command}
        >
          <Typography
            sx={{
              width: "100%",
              textJustify: "center",
              textAlign: "center",
            }}
            variant={"body2"}
          >
            {command ? commandState?.[command]?.action : ""}
          </Typography>
        </SlideToConfirm>
      </Collapse>
    </>
  );
};
